import type { CSSProperties, ReactNode } from 'react';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { useTopBarSlots } from '@/components/TopBarSlots';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';

/**
 * Drag/no-drag region styles for Electron's frameless / hiddenInset
 * windows. `WebkitAppRegion: 'drag'` turns the element into the
 * window's drag handle; `'no-drag'` opts an inner element back out
 * (so buttons inside the topbar still receive clicks instead of
 * dragging the window).
 *
 * Outside Electron the property is ignored entirely (browsers don't
 * recognize the `-webkit-app-region` property), so applying these
 * unconditionally is safe — no need to platform-gate.
 */
const dragStyle: CSSProperties = { WebkitAppRegion: 'drag' } as CSSProperties;
const noDragStyle: CSSProperties = { WebkitAppRegion: 'no-drag' } as CSSProperties;

/**
 * Glass redesign — Phase 1.
 *
 * 52px-tall translucent topbar with:
 *   - Sidebar collapse trigger (existing shadcn)
 *   - `left` slot — usually breadcrumbs, page title, or both
 *   - `right` slot — page-specific actions (search, filter, view
 *     switcher, CTAs). Theme toggle is appended automatically so
 *     every page gets it without having to re-render.
 *
 * The bar is `position: relative` with `backdrop-filter: blur(12px)`
 * and `z-index: 5` so glassy surfaces underneath show through but
 * popovers/menus that anchor to topbar buttons render above.
 *
 * `border-bottom` uses `--topbar-border` (= `--border`) for visual
 * continuity with the sidebar's right edge — the two combined form
 * an L-shape framing the content area.
 *
 * Future phases extend this with: command palette button (⌘K),
 * notifications bell (currently lives in sidebar footer — may relocate),
 * "New task" CTA. For Phase 1 we ship the structural shell + theme
 * toggle and let pages slot in their own actions.
 */
export interface AppTopBarProps {
  /**
   * Override the slot context entirely — used by surfaces outside
   * the workspace shell (e.g. login, onboarding) that render their
   * own AppTopBar inline. Inside the workspace shell, leave these
   * undefined and let pages contribute via `useSetTopBarSlots`.
   */
  left?: ReactNode;
  right?: ReactNode;
  cta?: ReactNode;
  className?: string;
}

export const AppTopBar = ({ left, right, cta, className }: AppTopBarProps) => {
  // Slot context wins over inline props when provided. The check
  // exists so non-shell consumers (no Provider in the tree) still
  // get sensible defaults — `useTopBarSlots()` returns nulls there.
  const slots = useTopBarSlots();
  const resolvedLeft = left ?? slots.left;
  const resolvedRight = right ?? slots.right;
  const resolvedCta = cta ?? slots.cta;

  return (
    <header
      data-shell-chrome
      className={cn(
        // 52px tall, flex row, sticky on top of content via z-5.
        // backdrop-blur lets the glassy column gradient bleed through.
        'relative z-[5] flex h-[52px] flex-shrink-0 items-center gap-3 border-b px-4',
        'bg-background/70 backdrop-blur-md',
        className,
      )}
      // Empty space in the topbar drags the Electron window. Interactive
      // children below opt back out via `noDragStyle`. Browser-side this
      // is a no-op.
      style={dragStyle}
    >
      <SidebarTrigger
        className="-ml-1 text-foreground-muted hover:text-foreground"
        style={noDragStyle}
      />

      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden" style={noDragStyle}>
        {resolvedLeft}
      </div>

      {/*
        Right cluster, ordered to match the prototype's topbar:
        [right slot]  [theme toggle]  [cta slot]
        = Search / Filter / Avatars / ViewSwitcher  ·  Sun/Moon  ·  + New task
      */}
      <div className="flex flex-shrink-0 items-center gap-2" style={noDragStyle}>
        {resolvedRight}
        <ThemeToggle />
        {resolvedCta}
      </div>
    </header>
  );
};
